import api from "./api";
import type {
  ApiResponse,
  PaginationMeta,
} from "@/types/api.types";
import type {
  Contract,
  ContractFormData,
  ContractFilters,
  ContractAttachment,
  ContractIssuedPdf,
  ContractFulfillmentSummary,
  CreateSalesOrderFromContractPayload,
} from "@/types/contract.types";
import type { SalesOrder } from "@/types/selling.types";

class ContractService {
  /** API allows page_size 1–100; values above 100 return 422. */
  static readonly MAX_PAGE_SIZE = 100;

  private normalizeIssuedPdf(raw: unknown): ContractIssuedPdf | null {
    if (!raw || typeof raw !== "object") return null;
    const pdf = raw as Record<string, unknown>;
    const id = pdf.id != null ? String(pdf.id) : "";
    if (!id) return null;
    return {
      id,
      filename: String(pdf.filename ?? ""),
      file_size: Number(pdf.file_size ?? 0),
      stored_at: String(pdf.stored_at ?? ""),
    };
  }

  private issuedPdfFromAttachments(attachments: unknown): ContractIssuedPdf | null {
    if (!Array.isArray(attachments)) return null;
    const issued = attachments.find(
      (row) =>
        row &&
        typeof row === "object" &&
        ((row as ContractAttachment).kind === "issued" ||
          (row as ContractAttachment).locked),
    ) as ContractAttachment | undefined;
    if (!issued) return null;
    return {
      id: String(issued.id),
      filename: issued.filename,
      file_size: Number(issued.file_size ?? 0),
      stored_at: String(issued.uploaded_at ?? ""),
    };
  }

  private normalizeContract(raw: Record<string, unknown>): Contract {
    const attachments = Array.isArray(raw.attachments)
      ? (raw.attachments as ContractAttachment[])
      : [];
    const issued_pdf =
      this.normalizeIssuedPdf(raw.issued_pdf) ??
      this.issuedPdfFromAttachments(attachments);
    const issuedId = issued_pdf?.id;
    return {
      ...(raw as unknown as Contract),
      attachments: issuedId
        ? attachments.filter((row) => String(row.id) !== issuedId)
        : attachments.filter((row) => row.kind !== "issued" && !row.locked),
      issued_pdf,
    };
  }

  async getContracts(
    params?: ContractFilters & { page?: number; page_size?: number },
  ): Promise<{ data: Contract[]; meta: PaginationMeta }> {
    const page_size = Math.min(params?.page_size ?? 10, ContractService.MAX_PAGE_SIZE);
    const response = await api.get<any>("/api/v1/contracts/", {
      params: { ...params, page_size },
    });
    const r = response.data;
    return {
      data: r.data ?? [],
      meta: {
        current_page: r.page ?? 1,
        per_page: r.per_page ?? 10,
        total: r.total ?? 0,
        last_page: r.total_pages ?? 1,
      },
    };
  }

  async getContract(id: string): Promise<Contract> {
    const response = await api.get<Contract>(`/api/v1/contracts/${id}`);
    return this.normalizeContract(response.data as unknown as Record<string, unknown>);
  }

  async createContract(data: ContractFormData): Promise<Contract> {
    const response = await api.post<Contract>("/api/v1/contracts/", data);
    return response.data;
  }

  async updateContract(
    id: string,
    data: Partial<ContractFormData>,
  ): Promise<Contract> {
    const response = await api.put<Contract>(`/api/v1/contracts/${id}`, data);
    return response.data;
  }

  async deleteContract(id: string): Promise<void> {
    await api.delete(`/api/v1/contracts/${id}`);
  }

  async submitContract(id: string): Promise<Contract> {
    const response = await api.post<Contract>(`/api/v1/contracts/${id}/submit`);
    return response.data;
  }

  async cancelContract(id: string): Promise<Contract> {
    const response = await api.post<Contract>(`/api/v1/contracts/${id}/cancel`);
    return response.data;
  }

  async uploadAttachment(id: string, file: File): Promise<ContractAttachment> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<ContractAttachment | ApiResponse<ContractAttachment>>(
      `/api/v1/contracts/${id}/attachments`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    const body = response.data as ContractAttachment | ApiResponse<ContractAttachment>;
    return "data" in body && body.data ? body.data : (body as ContractAttachment);
  }

  async deleteAttachment(
    contractId: string,
    attachmentId: string | number,
  ): Promise<void> {
    await api.delete(
      `/api/v1/contracts/${contractId}/attachments/${String(attachmentId)}`,
    );
  }

  async getContractsByProject(projectId: string): Promise<Contract[]> {
    const response = await api.get<{ data?: Contract[] }>(
      `/api/v1/projects/${projectId}/contracts`,
    );
    const list = response.data?.data;
    return Array.isArray(list) ? list : [];
  }

  async updateStatus(id: string, status: string): Promise<Contract> {
    const STATUS_TO_INT: Record<string, number> = {
      draft: 1,
      active: 2,
      expired: 3,
      cancelled: 4,
      fulfilled: 5,
    };
    const code = STATUS_TO_INT[status.toLowerCase()] ?? Number(status);
    if (!Number.isFinite(code) || code < 1) {
      throw new Error(`Invalid contract status: ${status}`);
    }
    const response = await api.patch<Contract>(`/api/v1/contracts/${id}/status`, {
      status: code,
    });
    return response.data;
  }

  async generatePDF(id: string): Promise<Blob> {
    const response = await api.get(`/api/v1/contracts/${id}/pdf`, {
      responseType: "blob",
    });
    return response.data;
  }

  async reprintPdf(id: string): Promise<Contract> {
    const response = await api.post<Contract>(`/api/v1/contracts/${id}/reprint`);
    return this.normalizeContract(response.data as unknown as Record<string, unknown>);
  }

  async getContractLocations(contractId: string): Promise<any[]> {
    const response = await api.get<any>(`/api/v1/contracts/${contractId}/locations`);
    return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
  }

  async linkContractLocation(
    contractId: string,
    locationId: number,
    crLicenseId?: string
  ): Promise<void> {
    await api.post(`/api/v1/contracts/${contractId}/locations`, {
      location_id: locationId,
      cr_license_id: crLicenseId,
    });
  }

  async unlinkContractLocation(contractId: string, locationId: number): Promise<void> {
    await api.delete(`/api/v1/contracts/${contractId}/locations/${locationId}`);
  }

  async createProjectFromContract(
    contractId: string,
    data: {
      cr_licence_id: string;
      project_type?: number;
      location?: string;
      location_id?: number | null;
    },
  ): Promise<{ id: string }> {
    const response = await api.post<any>(
      `/api/v1/contracts/${contractId}/create-project`,
      data,
    );
    return response.data;
  }

  async getContractSalesOrders(
    contractId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<SalesOrder[]> {
    const response = await api.get<{ data?: SalesOrder[] }>(
      `/api/v1/contracts/${contractId}/sales-orders`,
      { params: { page_size: 50, ...params } },
    );
    const rows = response.data?.data;
    return Array.isArray(rows) ? rows : [];
  }

  async getContractFulfillment(contractId: string): Promise<ContractFulfillmentSummary> {
    const response = await api.get<ContractFulfillmentSummary>(
      `/api/v1/contracts/${contractId}/fulfillment`,
    );
    return response.data;
  }

  async createSalesOrderFromContractLines(
    contractId: string,
    payload: CreateSalesOrderFromContractPayload,
  ): Promise<SalesOrder> {
    const response = await api.post<SalesOrder>(
      `/api/v1/contracts/${contractId}/sales-orders/generate`,
      {
        ...payload,
        order_date: payload.order_date ?? new Date().toISOString().split("T")[0],
      },
    );
    return response.data;
  }

  /** @deprecated Use createSalesOrderFromContractLines for contracts with lines */
  async createSalesOrderFromContract(
    contractId: string,
    customerId: string,
  ): Promise<{ id: string }> {
    const response = await api.post<any>(
      `/api/v1/contracts/${contractId}/sales-orders/generate`,
      {
        mode: "full",
        customer_id: customerId,
        order_date: new Date().toISOString().split("T")[0],
      },
    );
    return { id: response.data.id };
  }
}

export default new ContractService();
